/* eslint-disable camelcase */

/** @jsxFrag React.Fragment */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';
import { AiCoach_Action, AiCoach_Mode } from '__generated__/graphql-types';
import type { AiCoach_RecapMetadataResult } from '__generated__/graphql-types';

import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';

import type {
  AiCoach_RecapMetadataResultQuery,
  AiCoach_RecapMetadataResultQueryVariables,
} from 'bundles/ai-course-coach/queries/__generated__/aiCoachRecapMetadataByCourseId';
import aiCoachRecapMetadataByCourseIdQuery from 'bundles/ai-course-coach/queries/aiCoachRecapMetadataByCourseId.graphql';
import { firstNameFromFullName } from 'bundles/ai-course-coach/utils/nameParser';
import { isCoachRecapEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';

import _t from 'i18n!nls/learning-assistant-chat-ai';

const recapActionButtons = [
  {
    label: _t('Recap what I learned'),
    isCoachTrigger: true,
    action: AiCoach_Action.Recap,
    mode: AiCoach_Mode.CourseGuide,
  },
  {
    label: _t("Let's chat"),
    isCoachTrigger: true,
    action: AiCoach_Action.GenericAction,
    mode: AiCoach_Mode.CourseGuide,
  },
];

const RecapDisclosureMessage = () => {
  const { fullName } = getUser();
  const learnerFirstName = firstNameFromFullName(fullName);

  return (
    <>
      <strong>
        {_t(
          `Welcome back, #{learnerFirstName}! Reviewing what you learned during your last session can spark today's learning.`,
          { learnerFirstName }
        )}
      </strong>
      <Typography2
        variant="bodyPrimary"
        component="p"
        css={css`
          margin: var(--cds-spacing-200) 0 0 0;
        `}
      >
        {_t('How may I help you pick up where you left off?')}
      </Typography2>
    </>
  );
};

export const getCoachRecapDisclosureData = () => {
  return {
    actionButtons: recapActionButtons,
    message: <RecapDisclosureMessage />,
  };
};

type RecapData = Omit<AiCoach_RecapMetadataResult, '__typename' | 'numItemsCompleted'> | null;

/**
 * Hook to query recap metadata using Apollo Client
 * @param courseId the courseId to fetch recap for
 * @param shouldFetch whether to skip fetching e.g. skip for other ExternalDisclosure usages outside Week page
 */
const useRecapMetadataQuery = ({ courseId, shouldFetch = false }: { courseId?: string; shouldFetch?: boolean }) => {
  const { loading, error, data } = useQuery<
    AiCoach_RecapMetadataResultQuery,
    AiCoach_RecapMetadataResultQueryVariables
  >(aiCoachRecapMetadataByCourseIdQuery, {
    context: { clientName: 'gatewayGql' },
    variables: { courseId: courseId as string },
    skip: !courseId || !shouldFetch,
  });

  let recapData: RecapData = null;

  if (data?.AiCoach?.queryRecapMetadataByCourseId) {
    recapData = {
      displayRecap: data?.AiCoach?.queryRecapMetadataByCourseId.displayRecap,
    };
  }

  if (error) {
    captureMessageInSentry({ error, source: 'recap', extra: { courseId } });
  }

  return { recapLoading: loading, recapError: error, recapData };
};

/**
 * Hook to fetch Coach recap data, see [COACH-125](https://coursera.atlassian.net/browse/COACH-125)
 *
 * @param courseId the courseId to fetch recap for
 * @param shouldShowRecap whether recap should be shown, based on week data from PeriodPage
 */
const useCoachRecap = ({ courseId, shouldShowRecap }: { courseId?: string; shouldShowRecap: boolean }) => {
  const { recapLoading, recapError, recapData } = useRecapMetadataQuery({
    courseId,
    shouldFetch: isCoachRecapEnabled(courseId) && shouldShowRecap,
  });

  const hasRecap = shouldShowRecap && !!recapData?.displayRecap;

  return {
    recapError,
    recapLoading,
    hasRecap,
    disclosureData: hasRecap ? getCoachRecapDisclosureData() : null,
  };
};

export { useCoachRecap, useRecapMetadataQuery };
